/**
 * @module @shared/api/legal
 * Boundary-Vertrag für `legal.block_versions` — die **base-Seite** des 3-Wege-Merge.
 *
 * `@repo/shared-types/legal` (LEG-002) beschreibt Bausteine, Profile und Overrides,
 * aber noch KEINE Zeile der Versionshistorie. Statt einen zweiten Typ zu schreiben,
 * wird das Schema hier aus `LegalBlockSchema` **abgeleitet** (`.pick()` + `.extend()`):
 * `titleDe`/`bodyDe`/`version` bleiben damit an der SSOT hängen, und ein Feldwechsel
 * dort bricht hier sofort den Typecheck.
 *
 * Fällig, sobald `packages/shared-types` wieder angefasst wird: `LegalBlockVersionSchema`
 * gehört in `packages/shared-types/src/legal.ts` (SDK und Admin brauchen es beide) —
 * diese Datei ist die Übergangslösung, weil LEG-004 ausschließlich `apps/admin` ändert.
 */
import { LegalBlockSchema } from '@repo/shared-types';
import { z } from 'zod';

/**
 * Eine archivierte Baustein-Fassung (`legal.block_versions`). Append-only; die
 * AKTUELLE Fassung steht nicht hier, sondern in `legal.blocks` (siehe Migration `0013`).
 */
export const LegalBlockVersionSchema = LegalBlockSchema.pick({
  titleDe: true,
  bodyDe: true,
}).extend({
  /** Fremdschlüssel auf `legal.blocks.key` — gleiche Form wie dort. */
  blockKey: LegalBlockSchema.shape.key,
  /** Die archivierte `legal.blocks.version`. */
  version: LegalBlockSchema.shape.version,
});

/** Eine Zeile der Baustein-Historie. */
export type LegalBlockVersion = z.infer<typeof LegalBlockVersionSchema>;

/**
 * Sucht die archivierte Fassung eines Bausteins — die `base`-Spalte des 3-Wege-Diffs.
 *
 * @param versions Alle bekannten Historien-Zeilen.
 * @param blockKey Schlüssel des Bausteins.
 * @param version Die gesuchte (geforkte) Version.
 * @returns Die Fassung oder `undefined`, wenn die Historie sie nicht (mehr) enthält.
 */
export function findBlockVersion(
  versions: readonly LegalBlockVersion[],
  blockKey: string,
  version: number,
): LegalBlockVersion | undefined {
  return versions.find((entry) => entry.blockKey === blockKey && entry.version === version);
}
